import React, { useEffect, useState, useCallback } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link, useHistory, useParams } from "react-router-dom";

import Layout from "../../../../../../../components/Layout";
import Container from "../../../../../../../components/Container";
import Flex from "../../../../../../../components/Flex";
import Content from "../../../../../../../components/Content";
import Button from "../../../../../../../components/Button";
import ButtonList from "../../../../../../../components/ButtonList";
import Breadcrumbs from "../../../../../../../components/Breadcrumbs";
import { useMessageToast } from "../../../../../../../components/MessageProvider";
import { useFetch } from "../../../../../../../components/useFetch";
import Label from "../../../../../../../components/Label";
import DropDownList from "../../../../../../../components/DropDownList";
import Spinner from "../../../../../../../components/Loader/Spinner";
import TextBox from "../../../../../../../components/TextBox";
import errorMessageHandler from "../../../../../errorMessageHandler";

import styles from "./AddEditElementTrust.module.scss";

const AddEditElementTrust = () => {
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const organizationApiUrl = `${clientConfigApiDomain}/organizations`;
  const clientsApiUrl = `${clientConfigApiDomain}/clients`;
  const sourcesApiUrl = `${clientConfigApiDomain}/sources`;
  const elementTrustScoreApiUrl = `${clientConfigApiDomain}/element_trust_scores`;

  const { id, clientId, sourceId, elementTrustId } = useParams();

  const history = useHistory();
  const toast = useMessageToast();

  const [orgData, setOrgData] = useState({});
  const [clientData, setClientData] = useState({});
  const [sourceData, setSourceData] = useState({});
  const [elementTrustData, setElementTrustData] = useState([]);
  const [elementTrustNameList, setElementTrustNameList] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [
    { hasLoaded: addEditHasLoaded, hasError: addEditHasError, error },
    addEditFetch
  ] = useFetch();

  const fetchData = useCallback(async () => {
    const orgResponse = await fetch(`${organizationApiUrl}/${id}/`);
    const orgData = await orgResponse.json();
    setOrgData(orgData);

    const clientResponse = await fetch(`${clientsApiUrl}/${clientId}/`);
    const clientData = await clientResponse.json();
    setClientData(clientData);

    const sourceResponse = await fetch(`${sourcesApiUrl}/${sourceId}/`);
    const sourceData = await sourceResponse.json();
    setSourceData(sourceData);

    if (elementTrustId !== undefined) {
      for (let etd of sourceData.element_trust_scores) {
        if (etd.id === Number(elementTrustId)) {
          setElementTrustData(etd);
        }
      }
    }

    const elementTrustNameMasterList = [
      "address",
      "birthDate",
      "birthPlace",
      "cadavericDonor",
      "communication",
      "contact",
      "deceased[x]",
      "disability",
      "generalPractitioner",
      "gender",
      "managingOrganization",
      "maritalStatus",
      "multipleBirth[x]",
      "name",
      "photo",
      "religion",
      "telecom",
      "us_core_ethnicity",
      "us_core_race"
    ];
    const sourceElementTrustNameList = sourceData.element_trust_scores.map(
      ets => ets.element
    );
    setElementTrustNameList(
      elementTrustNameMasterList.filter(
        etn => !sourceElementTrustNameList.includes(etn)
      )
    );

    setHasLoaded(true);
  }, [
    id,
    clientId,
    sourceId,
    elementTrustId,
    organizationApiUrl,
    clientsApiUrl,
    sourcesApiUrl
  ]);

  useEffect(() => {
    if (!hasLoaded) {
      fetchData();
    } else {
      setHasLoaded(true);
    }
  }, [hasLoaded, fetchData]);

  useEffect(() => {
    if (elementTrustId === undefined && addEditHasLoaded) {
      if (addEditHasLoaded) {
        toast.success(`Element trust score added successfully.`);
        history.push(
          `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}`
        );
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error(`Failed to add element trust score.`);
      }
    } else {
      if (addEditHasLoaded) {
        toast.success(`Element trust score updated successfully.`);
        history.push(
          `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}`
        );
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error(`Failed to update this element trust score.`);
      }
    }
  }, [
    id,
    clientId,
    sourceId,
    elementTrustId,
    addEditHasError,
    addEditHasLoaded,
    toast,
    history,
    error
  ]);

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          {!hasLoaded ? <Spinner /> : null}
          {hasLoaded ? (
            <Breadcrumbs>
              <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
              <Breadcrumbs.Item
                render={() => <Link to="/admin">Administration</Link>}
              />
              <Breadcrumbs.Item
                render={() => (
                  <Link to="/admin/organizations">Organizations</Link>
                )}
              />
              <Breadcrumbs.Item
                render={() => (
                  <Link to={`/admin/organizations/${orgData.id}`}>
                    Organization: {orgData.name}
                  </Link>
                )}
              />
              <Breadcrumbs.Item
                render={() => (
                  <Link
                    to={`/admin/organizations/${orgData.id}/accounts/${clientData.id}`}
                  >
                    Account: {clientData.name}
                  </Link>
                )}
              />
              <Breadcrumbs.Item
                render={() => (
                  <Link
                    to={`/admin/organizations/${orgData.id}/accounts/${clientData.id}/sources/${sourceData.id}`}
                  >
                    Source: {sourceData.name}
                  </Link>
                )}
              />
              {elementTrustId !== undefined ? (
                <Breadcrumbs.Item
                  last
                  render={() => "Edit Element Trust Score"}
                />
              ) : (
                <Breadcrumbs.Item
                  last
                  render={() => "Add Element Trust Score"}
                />
              )}
            </Breadcrumbs>
          ) : null}
          {hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                {elementTrustId !== undefined ? (
                  <h1>Edit Element Trust Score: {elementTrustData.element}</h1>
                ) : (
                  <h1>Add Element Trust Score</h1>
                )}
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}></div>
                <div className={styles.details}>
                  <Formik
                    initialValues={{
                      elementTrustName: elementTrustData.element,
                      elementTrustScore: elementTrustData.trust_score,
                      source: elementTrustData.source
                    }}
                    enableReinitialize={true}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      setSubmitting(true);
                      let data = {
                        element: values.elementTrustName,
                        trust_score: values.elementTrustScore,
                        source: sourceData.url
                      };
                      let method = "";
                      let url = "";
                      if (elementTrustId) {
                        data["id"] = elementTrustId;
                        method = "put";
                        url = `${elementTrustScoreApiUrl}/${elementTrustId}/`;
                      } else {
                        method = "post";
                        url = `${elementTrustScoreApiUrl}/`;
                      }
                      addEditFetch({ method, url, data });
                      setSubmitting(false);
                    }}
                    validationSchema={Yup.object().shape({
                      elementTrustName: Yup.string()
                        .trim()
                        .required("The element trust name is required"),
                      elementTrustScore: Yup.string()
                        .trim()
                        .required("The element trust score is required")
                        .matches(/^[0-9]*\.?[0-9]*$/, "invalid number format")
                    })}
                  >
                    {props => {
                      const {
                        values,
                        touched,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit
                      } = props;

                      return (
                        <form onSubmit={handleSubmit}>
                          <div>
                            {elementTrustId === undefined ? (
                              <DropDownList
                                errorText={errors.elementTrustName}
                                id="elementTrustName"
                                label={
                                  <Label
                                    error={
                                      touched.elementTrustName &&
                                      errors.elementTrustName
                                    }
                                    htmlFor="elementTrustName"
                                  >
                                    <b>Element Name</b>
                                  </Label>
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.client}
                                width="25%"
                              >
                                <DropDownList.Item value="">
                                  Choose an element name...
                                </DropDownList.Item>
                                {elementTrustNameList.map(etn => (
                                  <DropDownList.Item key={etn} value={etn}>
                                    {etn}
                                  </DropDownList.Item>
                                ))}
                              </DropDownList>
                            ) : null}
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.elementTrustScore
                                  ? errors.elementTrustScore
                                  : null
                              }
                              id="elementTrustScore"
                              label={
                                <Label
                                  error={
                                    touched.elementTrustScore &&
                                    errors.elementTrustScore
                                  }
                                  htmlFor="elementTrustScore"
                                >
                                  <b>Trust Score</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.elementTrustScore}
                              width="25%"
                            />
                          </div>
                          <ButtonList>
                            <Button primary type="submit">
                              {elementTrustId !== undefined
                                ? "Save Changes"
                                : "Save"}
                            </Button>
                            <Button
                              onClick={() => {
                                history.push(
                                  `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}/`
                                );
                              }}
                            >
                              Cancel
                            </Button>
                          </ButtonList>
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default AddEditElementTrust;
